import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import type { DocumentMinimalData, ProviderDatabase } from '../../../../types';
import { keys as providerKeys } from '../../../providers/api/keys';

const EMPTY_PROVIDERS: ProviderDatabase[] = [];
import { useGetProviders } from '../../../providers/api/useGetProviders';
import type { InitialQuoteReceivedValues } from '../../../topics/components/actionModales/AddQuoteReceivedActionModal/AddQuoteReceivedActionModal';
import { keys as topicKeys } from '../../../topics/api/keys';
import { keys as inboxKeys } from '../../api/keys';
import { useUpdateEmailMatchedTopic } from '../../api/useUpdateEmailMatchedTopic';
import { useUpdateEmailStatus } from '../../api/useUpdateEmailStatus';
import type { Civility } from '../../../providers/constants/civilities';
import { useUpdateEmailSuggestedActions } from '../../api/useUpdateEmailSuggestedActions';
import { getFinalizationState } from '../../utils/workflowSteps';
import type {
  AddCommunicationAction,
  CreateDocumentAction,
  CreateEventAction,
  CreatePendingItemAction,
  CreateProviderAction,
  CreateProviderContactAction,
  ExistingMatch,
  InboxEmail,
  SuggestedAction,
  TopicRecommendation,
} from '../../types/inbox.types';

import {
  parseIsoDate,
  type InitialContractFormValues,
} from '../../../providers/components/AddUpdateContractModal/useAddUpdateContractForm';

export type GroupedContact = {
  name: string;
  civility?: Civility | null;
  role?: string;
  phone?: string;
  email?: string;
  existingMatch?: ExistingMatch | null;
};

export type GroupedProvider = {
  providerName: string;
  description?: string;
  isExisting: boolean;
  existingProviderId?: string;
  contacts: GroupedContact[];
  isCompleted: boolean;
  isIgnored: boolean;
  sourceActions: (CreateProviderAction | CreateProviderContactAction)[];
};

export type ContractModalState = {
  action: CreateDocumentAction;
  providerId: string;
  showProviderSelect: boolean;
  initialValues: InitialContractFormValues;
  initialDocument: DocumentMinimalData;
};

type QuoteModalState = {
  action: CreateDocumentAction;
  subjectId: string;
  initialValues: InitialQuoteReceivedValues;
};

type EventModalState = {
  action: CreateEventAction;
  subTopicId: string;
  providerId?: string;
  initialValues: {
    title: string;
    description?: string;
    date: string;
    all_day: boolean;
    provider_name?: string;
  };
};

type PendingItemModalState = {
  action: CreatePendingItemAction;
  subjectId: string;
  initialTitle: string;
  initialProviderId?: string | null;
  initialProviderName?: string | null;
};

type Props = {
  email: InboxEmail;
  spaceId: string;
  onSuccess?: () => void;
};

export function useGuidedProcessingWorkflow(props: Props) {
  const queryClient = useQueryClient();
  const updateActionsMutation = useUpdateEmailSuggestedActions();
  const updateMatchedTopicMutation = useUpdateEmailMatchedTopic();
  const updateStatusMutation = useUpdateEmailStatus();
  const { data: existingProviders = EMPTY_PROVIDERS } = useGetProviders();

  const [currentSubTopicId, setCurrentSubTopicId] = useState<string>(
    props.email.matched_sub_topic_id || ''
  );
  const [manualCompletedOverrides, setManualCompletedOverrides] = useState<Record<number, boolean>>({});
  const [loadingActionKey, setLoadingActionKey] = useState<string | null>(null);

  // Modals state
  const [contractModalState, setContractModalState] = useState<ContractModalState | null>(null);
  const [quoteModalState, setQuoteModalState] = useState<QuoteModalState | null>(null);
  const [documentModalAction, setDocumentModalAction] = useState<CreateDocumentAction | null>(null);
  const [eventModalState, setEventModalState] = useState<EventModalState | null>(null);
  const [pendingItemModalState, setPendingItemModalState] = useState<PendingItemModalState | null>(null);
  const [isAddingPendingItem, setIsAddingPendingItem] = useState(false);
  const [isCommunicationModalOpen, setIsCommunicationModalOpen] = useState(false);
  const [isNewSubTopicModalOpen, setIsNewSubTopicModalOpen] = useState(false);

  // Sync subTopic when email changes
  useEffect(() => {
    setCurrentSubTopicId(props.email.matched_sub_topic_id || '');
  }, [props.email.id, props.email.matched_sub_topic_id]);

  const topicRecommendation = useMemo(() => {
    if (props.email.parsedTopicRecommendation) {
      return props.email.parsedTopicRecommendation;
    }
    if (props.email.topic_recommendation && typeof props.email.topic_recommendation === 'object') {
      return props.email.topic_recommendation as unknown as TopicRecommendation;
    }
    return null;
  }, [props.email.parsedTopicRecommendation, props.email.topic_recommendation]);

  // Extract actions by category
  const providerActions = useMemo(() => {
    return props.email.parsedActions.filter(
      (a): a is CreateProviderAction | CreateProviderContactAction =>
        a.type === 'CREATE_PROVIDER' || a.type === 'CREATE_PROVIDER_CONTACT'
    );
  }, [props.email.parsedActions]);

  const documentActions = useMemo(() => {
    return props.email.parsedActions.filter(
      (a): a is CreateDocumentAction => a.type === 'CREATE_DOCUMENT'
    );
  }, [props.email.parsedActions]);

  const contractActions = useMemo(() => {
    return documentActions.filter((a) => Boolean(a.isContract));
  }, [documentActions]);

  const quoteActions = useMemo(() => {
    return documentActions.filter((a) => Boolean(a.isQuote) && !a.isContract);
  }, [documentActions]);

  const otherDocActions = useMemo(() => {
    return documentActions.filter((a) => !a.isQuote && !a.isContract);
  }, [documentActions]);

  const eventActions = useMemo(() => {
    return props.email.parsedActions.filter(
      (a): a is CreateEventAction => a.type === 'CREATE_EVENT'
    );
  }, [props.email.parsedActions]);

  const pendingItemActions = useMemo(() => {
    return props.email.parsedActions.filter(
      (a): a is CreatePendingItemAction => a.type === 'CREATE_PENDING_ITEM'
    );
  }, [props.email.parsedActions]);

  const communicationAction = useMemo(() => {
    return props.email.parsedActions.find(
      (a): a is AddCommunicationAction => a.type === 'ADD_COMMUNICATION'
    );
  }, [props.email.parsedActions]);

  // Group providers and contacts into single cohesive provider fiches
  const groupedProviders = useMemo<GroupedProvider[]>(() => {
    const groups: GroupedProvider[] = [];

    // 1. Process CREATE_PROVIDER actions first
    for (const act of providerActions) {
      if (act.type === 'CREATE_PROVIDER') {
        const existing = existingProviders.find(
          (p) => p.name.toLowerCase().trim() === act.name.toLowerCase().trim()
        );

        const contacts: GroupedContact[] = [];
        if (act.contactName) {
          contacts.push({
            name: act.contactName,
            civility: act.contactCivility ?? null,
            role: 'Contact principal',
            phone: act.phone,
            email: act.email,
          });
        }

        groups.push({
          providerName: act.name,
          description: act.trade,
          isExisting: Boolean(existing),
          existingProviderId: existing?.id,
          contacts,
          isCompleted: Boolean(act.isCompleted),
          isIgnored: Boolean(act.isIgnored),
          sourceActions: [act],
        });
      }
    }

    // 2. Process CREATE_PROVIDER_CONTACT actions
    for (const act of providerActions) {
      if (act.type === 'CREATE_PROVIDER_CONTACT') {
        const targetName = (act.providerName || '').trim();
        const contactObj: GroupedContact = {
          name: act.name,
          civility: act.civility ?? null,
          role: act.role,
          phone: act.phone,
          email: act.email,
          existingMatch: act.existingMatch ?? null,
        };

        // Check if matches one of the CREATE_PROVIDER groups
        const matchedGroup = groups.find(
          (g) => g.providerName.toLowerCase().trim() === targetName.toLowerCase()
        );

        if (matchedGroup) {
          const exists = matchedGroup.contacts.some(
            (c) => c.name.toLowerCase().trim() === act.name.toLowerCase().trim()
          );
          if (!exists) {
            matchedGroup.contacts.push(contactObj);
          }
          matchedGroup.sourceActions.push(act);
          if (!act.isCompleted) {
            matchedGroup.isCompleted = false;
          }
          if (!act.isIgnored) {
            matchedGroup.isIgnored = false;
          }
        } else {
          // Check if matches an existing provider in db
          const existingProv = existingProviders.find(
            (p) =>
              p.id === act.providerId ||
              p.name.toLowerCase().trim() === targetName.toLowerCase()
          );

          const existingGroup = groups.find(
            (g) =>
              g.providerName.toLowerCase().trim() ===
              (existingProv?.name || targetName).toLowerCase().trim()
          );

          if (existingGroup) {
            existingGroup.contacts.push(contactObj);
            existingGroup.sourceActions.push(act);
            if (!act.isCompleted) {
              existingGroup.isCompleted = false;
            }
            if (!act.isIgnored) {
              existingGroup.isIgnored = false;
            }
          } else {
            groups.push({
              providerName: existingProv?.name || targetName || 'Prestataire',
              description: existingProv?.description || undefined,
              isExisting: Boolean(existingProv),
              existingProviderId: existingProv?.id,
              contacts: [contactObj],
              isCompleted: Boolean(act.isCompleted),
              isIgnored: Boolean(act.isIgnored),
              sourceActions: [act],
            });
          }
        }
      }
    }

    return groups;
  }, [providerActions, existingProviders]);

  // Derive step completion reactively from actions and manual overrides
  const autoCompletedSteps = useMemo(() => {
    const steps: number[] = [];
    if (groupedProviders.length > 0 && groupedProviders.every((g) => g.isCompleted || g.isIgnored)) {
      steps.push(1);
    }
    if (documentActions.length > 0 && documentActions.every((d) => d.isCompleted || d.isIgnored)) {
      steps.push(2);
    }
    if (eventActions.length > 0 && eventActions.every((e) => e.isCompleted || e.isIgnored)) {
      steps.push(3);
    }
    if (pendingItemActions.length > 0 && pendingItemActions.every((r) => r.isCompleted || r.isIgnored)) {
      steps.push(4);
    }
    if (communicationAction && (communicationAction.isCompleted || communicationAction.isIgnored)) {
      steps.push(5);
    }
    return steps;
  }, [groupedProviders, documentActions, eventActions, pendingItemActions, communicationAction]);

  const completedSteps = useMemo(() => {
    const set = new Set<number>(autoCompletedSteps);
    Object.entries(manualCompletedOverrides).forEach(([stepStr, isCompleted]) => {
      const stepNum = Number(stepStr);
      if (isCompleted) {
        set.add(stepNum);
      } else {
        set.delete(stepNum);
      }
    });
    return Array.from(set);
  }, [autoCompletedSteps, manualCompletedOverrides]);

  const finalizationState = getFinalizationState(props.email);

  async function handleSubTopicChange(newSubTopicId: string | null) {
    const subTopicIdVal = newSubTopicId || '';
    setCurrentSubTopicId(subTopicIdVal);
    try {
      await updateMatchedTopicMutation.mutateAsync({
        emailId: props.email.id,
        matchedSubTopicId: newSubTopicId,
      });
      enqueueSnackbar('Sujet associé mis à jour.', 'success');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du sujet associé:', err);
    }
  }

  function toggleStepCompleted(stepNumber: number) {
    const isCurrentlyCompleted = completedSteps.includes(stepNumber);
    setManualCompletedOverrides((prev) => ({
      ...prev,
      [stepNumber]: !isCurrentlyCompleted,
    }));
  }

  async function persistSpecificActionCompletion(action: SuggestedAction, isCompleted: boolean) {
    const updatedActions: SuggestedAction[] = props.email.parsedActions.map((act) => {
      const match =
        act.type === action.type &&
        (act.type === 'CREATE_DOCUMENT'
          ? (act as CreateDocumentAction).filename === (action as CreateDocumentAction).filename
          : act.type === 'CREATE_EVENT'
          ? (act as CreateEventAction).title === (action as CreateEventAction).title
          : act.type === 'CREATE_PENDING_ITEM'
          ? (act as CreatePendingItemAction).title === (action as CreatePendingItemAction).title
          : act.type === 'CREATE_PROVIDER'
          ? (act as CreateProviderAction).name === (action as CreateProviderAction).name
          : act.type === 'CREATE_PROVIDER_CONTACT'
          ? (act as CreateProviderContactAction).name === (action as CreateProviderContactAction).name
          : true);

      if (match) {
        return {
          ...act,
          isCompleted,
          completedAt: isCompleted ? new Date().toISOString() : undefined,
          isIgnored: isCompleted ? false : act.isIgnored,
          ignoredAt: isCompleted ? undefined : act.ignoredAt,
        };
      }
      return act;
    });

    try {
      await updateActionsMutation.mutateAsync({
        emailId: props.email.id,
        suggestedActions: updatedActions,
      });
      queryClient.invalidateQueries({ queryKey: inboxKeys.inbox });
    } catch (err) {
      console.error('Erreur lors de la mise à jour des actions:', err);
    }
  }

  async function persistSpecificActionIgnore(action: SuggestedAction, isIgnored: boolean) {
    const updatedActions: SuggestedAction[] = props.email.parsedActions.map((act) => {
      const match =
        act.type === action.type &&
        (act.type === 'CREATE_DOCUMENT'
          ? (act as CreateDocumentAction).filename === (action as CreateDocumentAction).filename
          : act.type === 'CREATE_EVENT'
          ? (act as CreateEventAction).title === (action as CreateEventAction).title
          : act.type === 'CREATE_PENDING_ITEM'
          ? (act as CreatePendingItemAction).title === (action as CreatePendingItemAction).title
          : act.type === 'CREATE_PROVIDER'
          ? (act as CreateProviderAction).name === (action as CreateProviderAction).name
          : act.type === 'CREATE_PROVIDER_CONTACT'
          ? (act as CreateProviderContactAction).name === (action as CreateProviderContactAction).name
          : true);

      if (match) {
        return {
          ...act,
          isIgnored,
          ignoredAt: isIgnored ? new Date().toISOString() : undefined,
          isCompleted: false,
          completedAt: undefined,
        };
      }
      return act;
    });

    try {
      await updateActionsMutation.mutateAsync({
        emailId: props.email.id,
        suggestedActions: updatedActions,
      });
      queryClient.invalidateQueries({ queryKey: inboxKeys.inbox });
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'action ignorée:", err);
    }
  }

  async function persistMultipleActionsCompletion(
    actions: (CreateProviderAction | CreateProviderContactAction)[],
    isCompleted: boolean
  ) {
    const actionMatchKeys = new Set(
      actions.map((a) => `${a.type}-${a.name}`)
    );

    const updatedActions: SuggestedAction[] = props.email.parsedActions.map((act) => {
      const actKey = `${act.type}-${(act as CreateProviderAction | CreateProviderContactAction).name || ''}`;
      if (actionMatchKeys.has(actKey)) {
        return {
          ...act,
          isCompleted,
          completedAt: isCompleted ? new Date().toISOString() : undefined,
          isIgnored: isCompleted ? false : act.isIgnored,
          ignoredAt: isCompleted ? undefined : act.ignoredAt,
        };
      }
      return act;
    });

    try {
      await updateActionsMutation.mutateAsync({
        emailId: props.email.id,
        suggestedActions: updatedActions,
      });
      queryClient.invalidateQueries({ queryKey: inboxKeys.inbox });
    } catch (err) {
      console.error('Erreur lors de la mise à jour des actions:', err);
    }
  }

  async function persistMultipleActionsIgnore(
    actions: (CreateProviderAction | CreateProviderContactAction)[],
    isIgnored: boolean
  ) {
    const actionMatchKeys = new Set(
      actions.map((a) => `${a.type}-${a.name}`)
    );

    const updatedActions: SuggestedAction[] = props.email.parsedActions.map((act) => {
      const actKey = `${act.type}-${(act as CreateProviderAction | CreateProviderContactAction).name || ''}`;
      if (actionMatchKeys.has(actKey)) {
        return {
          ...act,
          isIgnored,
          ignoredAt: isIgnored ? new Date().toISOString() : undefined,
          isCompleted: false,
          completedAt: undefined,
        };
      }
      return act;
    });

    try {
      await updateActionsMutation.mutateAsync({
        emailId: props.email.id,
        suggestedActions: updatedActions,
      });
      queryClient.invalidateQueries({ queryKey: inboxKeys.inbox });
    } catch (err) {
      console.error('Erreur lors de la mise à jour des actions:', err);
    }
  }

  // --- Step 1: Create Provider + All its contacts in one click ---
  async function handleCreateGroupedProvider(group: GroupedProvider) {
    const actionKey = `grouped-provider-${group.providerName}`;
    setLoadingActionKey(actionKey);
    try {
      let targetProviderId = group.existingProviderId;

      if (!targetProviderId) {
        // Create new provider
        const { data: newProvider, error: provError } = await apiClient
          .from('providers')
          .insert({
            space_id: props.spaceId,
            name: group.providerName,
            description: group.description || null,
          })
          .select('id')
          .single();

        if (provError) throw provError;
        targetProviderId = newProvider.id;
        queryClient.invalidateQueries({ queryKey: providerKeys.providers });
      }

      // Insert all contacts for this provider
      if (group.contacts.length > 0) {
        const contactsToInsert = group.contacts.map((c) => ({
          provider_id: targetProviderId!,
          name: c.name,
          civility: c.civility ?? null,
          role: c.role || 'Contact',
          phone1: c.phone || null,
          email: c.email || null,
        }));

        const { error: contactsError } = await apiClient
          .from('provider_contacts')
          .insert(contactsToInsert);

        if (contactsError) throw contactsError;
        queryClient.invalidateQueries({ queryKey: providerKeys.providers });
      }

      // Mark all source actions as completed
      await persistMultipleActionsCompletion(group.sourceActions, true);

      const msg = group.isExisting
        ? `Contact${group.contacts.length > 1 ? 's' : ''} ajouté${group.contacts.length > 1 ? 's' : ''} au prestataire « ${group.providerName} » !`
        : `Prestataire « ${group.providerName} » et ${group.contacts.length > 1 ? `ses ${group.contacts.length} contacts` : 'ses contacts'} créés avec succès !`;
      enqueueSnackbar(msg, 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la création du prestataire';
      enqueueSnackbar(msg, 'error');
    } finally {
      setLoadingActionKey(null);
    }
  }

  // --- Step 2: Documents & Devis execution ---
  async function handleOpenQuoteModal(targetAction: CreateDocumentAction) {
    const targetSubjectId = currentSubTopicId || targetAction.suggestedSubTopicId;
    if (!targetSubjectId) {
      enqueueSnackbar(
        'Veuillez sélectionner un sujet associé ci-dessus pour enregistrer ce devis.',
        'warning'
      );
      return;
    }

    if (!currentSubTopicId && targetAction.suggestedSubTopicId) {
      handleSubTopicChange(targetAction.suggestedSubTopicId);
    }

    const actionKey = `doc-${targetAction.filename}`;
    setLoadingActionKey(actionKey);
    try {
      let providerId: string | null = targetAction.suggestedProviderId || null;
      let defaultProviderSearch: string | null = null;

      if (!providerId && targetAction.suggestedProviderName) {
        const { data: matchedProvider } = await apiClient
          .from('providers')
          .select('id, name')
          .eq('space_id', props.spaceId)
          .ilike('name', targetAction.suggestedProviderName.trim())
          .maybeSingle();

        if (matchedProvider) {
          providerId = matchedProvider.id;
        } else {
          defaultProviderSearch = targetAction.suggestedProviderName.trim();
        }
      }

      let pendingFile: File | undefined = undefined;
      if (targetAction.storagePath) {
        try {
          const { data: fileBlob, error: downloadError } = await apiClient.storage
            .from('inbox-attachments')
            .download(targetAction.storagePath);

          if (!downloadError && fileBlob) {
            pendingFile = new File([fileBlob], targetAction.filename, {
              type: fileBlob.type || 'application/pdf',
            });
          }
        } catch (downloadErr) {
          console.warn('Impossible de télécharger le fichier du devis:', downloadErr);
        }
      }

      const initialDocuments: DocumentMinimalData[] = pendingFile
        ? [
            {
              id: crypto.randomUUID(),
              name: targetAction.filename,
              file_url: null,
              pendingFile,
              document_type_id: targetAction.suggestedDocumentTypeId || undefined,
              type: 'Devis',
              provider_id: providerId,
              provider_name: targetAction.suggestedProviderName || null,
            },
          ]
        : [];

      const quoteDescription =
        targetAction.description?.trim() ||
        (quoteActions.length === 1 ? props.email.document_summary?.trim() : null) ||
        targetAction.title ||
        props.email.subject ||
        '';

      const isAiGenerated = Boolean(
        targetAction.description?.trim() ||
        (quoteActions.length === 1 && props.email.document_summary?.trim())
      );

      const initialValues: InitialQuoteReceivedValues = {
        date: props.email.received_at ? new Date(props.email.received_at) : new Date(),
        amount: targetAction.suggestedAmount ?? null,
        comment: quoteDescription,
        isAiGeneratedDescription: isAiGenerated,
        provider_id: providerId,
        defaultProviderSearch,
        documents: initialDocuments,
      };

      setQuoteModalState({
        action: targetAction,
        subjectId: targetSubjectId,
        initialValues,
      });
    } catch (err: unknown) {
      console.error('Erreur préparation devis:', err);
      enqueueSnackbar('Erreur lors de la préparation du devis', 'error');
    } finally {
      setLoadingActionKey(null);
    }
  }

  function handleCloseQuoteModal() {
    setQuoteModalState(null);
  }

  async function handleQuoteModalSuccess() {
    if (quoteModalState?.action) {
      await persistSpecificActionCompletion(quoteModalState.action, true);
    }
    setQuoteModalState(null);
    enqueueSnackbar('Devis enregistré avec succès !', 'success');
  }

  async function handleOpenContractModal(targetAction: CreateDocumentAction) {
    const actionKey = `contract-${targetAction.filename}`;
    setLoadingActionKey(actionKey);
    try {
      let providerId: string | null = targetAction.suggestedProviderId || null;

      if (!providerId && targetAction.suggestedProviderName) {
        const { data: matchedProvider } = await apiClient
          .from('providers')
          .select('id, name')
          .eq('space_id', props.spaceId)
          .ilike('name', targetAction.suggestedProviderName.trim())
          .maybeSingle();

        if (matchedProvider) {
          providerId = matchedProvider.id;
        }
      }

      let pendingFile: File | undefined = undefined;
      if (targetAction.storagePath) {
        try {
          const { data: fileBlob, error: downloadError } = await apiClient.storage
            .from('inbox-attachments')
            .download(targetAction.storagePath);

          if (!downloadError && fileBlob) {
            pendingFile = new File([fileBlob], targetAction.filename, {
              type: fileBlob.type || 'application/pdf',
            });
          }
        } catch (downloadErr) {
          console.warn('Impossible de télécharger le fichier du contrat:', downloadErr);
        }
      }

      const cd = targetAction.contractData;
      const initialValues: InitialContractFormValues = {
        name: cd?.name || targetAction.title || targetAction.filename.replace(/\.[^/.]+$/, ''),
        provider_id: providerId,
        start_at: cd?.start_at ? parseIsoDate(cd.start_at) : null,
        end_at: cd?.end_at ? parseIsoDate(cd.end_at) : null,
        has_automatic_renewal: cd?.has_automatic_renewal ?? true,
        duration_of_renewal: cd?.duration_of_renewal ?? 1,
        period_of_notice: cd?.period_of_notice ?? null,
        price: cd?.price ?? (targetAction.suggestedAmount ? `${targetAction.suggestedAmount} € / an` : null),
        description: cd?.description || targetAction.description || null,
      };

      const initialDocument: DocumentMinimalData = {
        id: crypto.randomUUID(),
        name: initialValues.name || targetAction.filename,
        file_url: null,
        pendingFile,
        type: 'Contrat',
        provider_id: providerId,
      };

      setContractModalState({
        action: targetAction,
        providerId: providerId || '',
        showProviderSelect: !providerId,
        initialValues,
        initialDocument,
      });
    } catch (err) {
      console.error('Erreur lors de la préparation du contrat:', err);
      enqueueSnackbar('Impossible de charger le contrat.', 'error');
    } finally {
      setLoadingActionKey(null);
    }
  }

  function handleCloseContractModal() {
    setContractModalState(null);
  }

  async function handleContractModalSuccess() {
    if (contractModalState?.action) {
      await persistSpecificActionCompletion(contractModalState.action, true);
    }
    setContractModalState(null);
    enqueueSnackbar('Contrat enregistré avec succès !', 'success');
  }

  function handleOpenDocumentModal(action: CreateDocumentAction) {
    setDocumentModalAction({
      ...action,
      suggestedSubTopicId: currentSubTopicId || action.suggestedSubTopicId,
    });
  }

  function handleCloseDocumentModal() {
    setDocumentModalAction(null);
  }

  async function handleDocumentModalSuccess() {
    if (documentModalAction) {
      await persistSpecificActionCompletion(documentModalAction, true);
    }
    setDocumentModalAction(null);
  }

  async function handlePreviewDocument(storagePath: string) {
    try {
      const { data, error } = await apiClient.storage
        .from('inbox-attachments')
        .createSignedUrl(storagePath, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Impossible de prévisualiser le document';
      enqueueSnackbar(msg, 'error');
    }
  }

  // --- Step 3: RDV execution ---
  function handleOpenEventModal(action: CreateEventAction) {
    const targetSubTopicId = currentSubTopicId || action.suggestedSubTopicId || '';
    if (!targetSubTopicId) {
      enqueueSnackbar(
        'Veuillez sélectionner un sujet associé ci-dessus pour planifier ce rendez-vous.',
        'warning'
      );
      return;
    }

    if (!currentSubTopicId && action.suggestedSubTopicId) {
      handleSubTopicChange(action.suggestedSubTopicId);
    }

    let targetProviderId: string | undefined = action.suggestedProviderId || undefined;
    if (!targetProviderId && action.suggestedProviderName) {
      const found = existingProviders.find(
        (p) => p.name.toLowerCase().trim() === action.suggestedProviderName!.toLowerCase().trim()
      );
      if (found) {
        targetProviderId = found.id;
      }
    }

    setEventModalState({
      action,
      subTopicId: targetSubTopicId,
      providerId: targetProviderId,
      initialValues: {
        title: action.title,
        description: action.description || '',
        date: action.date,
        all_day: false,
        provider_name: action.suggestedProviderName || undefined,
      },
    });
  }

  function handleCloseEventModal() {
    setEventModalState(null);
  }

  async function handleEventModalSuccess() {
    if (eventModalState?.action) {
      await persistSpecificActionCompletion(eventModalState.action, true);
    }
    setEventModalState(null);
  }

  // --- Step 4: Reminder execution ---
  // --- Step 5: Pending Item (ressource à suivre) execution ---
  function handleOpenPendingItemModal(action: CreatePendingItemAction) {
    const targetSubTopicId = currentSubTopicId || action.suggestedSubTopicId || '';
    if (!targetSubTopicId) {
      enqueueSnackbar(
        'Veuillez sélectionner un sujet associé ci-dessus pour ajouter cet élément à suivre.',
        'warning'
      );
      return;
    }

    if (!currentSubTopicId && action.suggestedSubTopicId) {
      handleSubTopicChange(action.suggestedSubTopicId);
    }

    let targetProviderId: string | null = action.suggestedProviderId || null;
    if (!targetProviderId && action.suggestedProviderName) {
      const found = existingProviders.find(
        (p) => p.name.toLowerCase().trim() === action.suggestedProviderName!.toLowerCase().trim()
      );
      if (found) {
        targetProviderId = found.id;
      }
    }

    setPendingItemModalState({
      action,
      subjectId: targetSubTopicId,
      initialTitle: action.title,
      initialProviderId: targetProviderId,
      initialProviderName: action.suggestedProviderName || null,
    });
  }

  function handleClosePendingItemModal() {
    setPendingItemModalState(null);
  }

  async function handlePendingItemModalAdd(params: {
    title: string;
    providerId: string | null;
    providerName: string | null;
  }) {
    if (!pendingItemModalState) return;
    setIsAddingPendingItem(true);
    try {
      const { error } = await apiClient.from('subject_pending_items').insert({
        subject_id: pendingItemModalState.subjectId,
        title: params.title,
        provider_id: params.providerId,
        provider_name: params.providerName,
        state: 'pending',
        position: 0,
      });

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: topicKeys.getPendingItems(pendingItemModalState.subjectId),
      });
      if (props.spaceId) {
        queryClient.invalidateQueries({
          queryKey: topicKeys.pendingSubTopics(props.spaceId),
        });
        queryClient.invalidateQueries({
          queryKey: topicKeys.openPendingItems(props.spaceId),
        });
      }

      await persistSpecificActionCompletion(pendingItemModalState.action, true);
      setPendingItemModalState(null);
      enqueueSnackbar('Élément à suivre ajouté avec succès !', 'success');
    } catch (err: unknown) {
      console.error("Erreur lors de l'ajout de l'élément à suivre:", err);
      const msg = err instanceof Error ? err.message : "Erreur lors de l'ajout de l'élément à suivre";
      enqueueSnackbar(msg, 'error');
    } finally {
      setIsAddingPendingItem(false);
    }
  }

  async function handleDirectAddPendingItem(action: CreatePendingItemAction) {
    const targetSubTopicId = currentSubTopicId || action.suggestedSubTopicId || '';
    if (!targetSubTopicId) {
      enqueueSnackbar(
        'Veuillez sélectionner un sujet associé ci-dessus pour ajouter cet élément à suivre.',
        'warning'
      );
      return;
    }

    if (!currentSubTopicId && action.suggestedSubTopicId) {
      handleSubTopicChange(action.suggestedSubTopicId);
    }

    let targetProviderId: string | null = action.suggestedProviderId || null;
    if (!targetProviderId && action.suggestedProviderName) {
      const found = existingProviders.find(
        (p) => p.name.toLowerCase().trim() === action.suggestedProviderName!.toLowerCase().trim()
      );
      if (found) {
        targetProviderId = found.id;
      }
    }

    setLoadingActionKey(`pending-${action.title}`);
    try {
      const { error } = await apiClient.from('subject_pending_items').insert({
        subject_id: targetSubTopicId,
        title: action.title,
        provider_id: targetProviderId,
        provider_name: action.suggestedProviderName || null,
        state: 'pending',
        position: 0,
      });

      if (error) throw error;

      queryClient.invalidateQueries({
        queryKey: topicKeys.getPendingItems(targetSubTopicId),
      });
      if (props.spaceId) {
        queryClient.invalidateQueries({
          queryKey: topicKeys.pendingSubTopics(props.spaceId),
        });
        queryClient.invalidateQueries({
          queryKey: topicKeys.openPendingItems(props.spaceId),
        });
      }

      await persistSpecificActionCompletion(action, true);
      enqueueSnackbar('Élément à suivre ajouté avec succès !', 'success');
    } catch (err: unknown) {
      console.error("Erreur lors de l'ajout de l'élément à suivre:", err);
      const msg = err instanceof Error ? err.message : "Erreur lors de l'ajout de l'élément à suivre";
      enqueueSnackbar(msg, 'error');
    } finally {
      setLoadingActionKey(null);
    }
  }

  // --- Step 4: Communication execution ---
  function handleOpenCommunicationModal() {
    if (!currentSubTopicId) {
      enqueueSnackbar(
        'Veuillez sélectionner un sujet associé ci-dessus pour ajouter une communication.',
        'warning'
      );
      return;
    }
    setIsCommunicationModalOpen(true);
  }

  function handleCloseCommunicationModal() {
    setIsCommunicationModalOpen(false);
  }

  async function handleCommunicationModalSuccess() {
    setIsCommunicationModalOpen(false);
    if (communicationAction) {
      await persistSpecificActionCompletion(communicationAction, true);
    }
    setManualCompletedOverrides((prev) => ({ ...prev, 5: true }));
    enqueueSnackbar('Communication enregistrée avec succès !', 'success');
  }

  // --- Final action: Finish & mark as processed ---
  async function handleCompleteAllAndProcess() {
    try {
      await updateStatusMutation.mutateAsync({
        emailId: props.email.id,
        spaceId: props.spaceId,
        status: 'processed',
      });
      enqueueSnackbar('Email traité avec succès !', 'success');
      props.onSuccess?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      enqueueSnackbar(msg, 'error');
    }
  }

  function handleOpenNewSubTopicModal() {
    setIsNewSubTopicModalOpen(true);
  }

  function handleCloseNewSubTopicModal() {
    setIsNewSubTopicModalOpen(false);
  }

  async function handleNewSubTopicSuccess(newSubTopicId: string) {
    await handleSubTopicChange(newSubTopicId);
  }

  return {
    currentSubTopicId,
    topicRecommendation,
    isNewSubTopicModalOpen,
    handleOpenNewSubTopicModal,
    handleCloseNewSubTopicModal,
    handleNewSubTopicSuccess,
    existingProviders,
    completedSteps,
    loadingActionKey,
    contractModalState,
    quoteModalState,
    documentModalAction,
    eventModalState,
    pendingItemModalState,
    isAddingPendingItem,
    isCommunicationModalOpen,
    providerActions,
    groupedProviders,
    documentActions,
    contractActions,
    quoteActions,
    otherDocActions,
    eventActions,
    pendingItemActions,
    communicationAction,
    handleSubTopicChange,
    toggleStepCompleted,
    persistSpecificActionCompletion,
    persistMultipleActionsCompletion,
    handleCreateGroupedProvider,
    handleOpenContractModal,
    handleCloseContractModal,
    handleContractModalSuccess,
    handleOpenQuoteModal,
    handleCloseQuoteModal,
    handleQuoteModalSuccess,
    handleOpenDocumentModal,
    handleCloseDocumentModal,
    handleDocumentModalSuccess,
    handlePreviewDocument,
    handleOpenEventModal,
    handleCloseEventModal,
    handleEventModalSuccess,
    handleOpenPendingItemModal,
    handleClosePendingItemModal,
    handlePendingItemModalAdd,
    handleDirectAddPendingItem,
    handleOpenCommunicationModal,
    handleCloseCommunicationModal,
    handleCommunicationModalSuccess,
    finalizationState,
    persistSpecificActionIgnore,
    persistMultipleActionsIgnore,
    handleCompleteAllAndProcess,
  };
}
